
import { RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  painelSurfaceClass,
  painelDescriptionClass,
  painelButtonOutlineClass,
} from '@/components/painel/painel-styles';
import { painelInputClass, painelTextareaClass } from '@/components/painel/cms/painel-form';
import { usePainelTextsAdmin } from '@/components/painel/painel-texts-context';
import {
  PAINEL_TEXT_ENTRIES,
  PAINEL_TEXT_SECTIONS,
  getPainelTextDefault,
} from '@/lib/painel-texts';

export function PainelTextosPage() {
  const { hydrated, getText, setText, resetText, isOverridden } = usePainelTextsAdmin();

  return (
    <div className="space-y-8">
      <div>
        <p className="font-inter text-[10px] font-bold uppercase tracking-[0.2em] text-sky-300/55">
          System
        </p>
        <h1 className="mt-1 font-poppins text-2xl font-bold text-white md:text-3xl">
          Textos do painel
        </h1>
        <p className={cn('mt-2 max-w-2xl text-sm', painelDescriptionClass)}>
          Edite os títulos e descrições internos de cada página do painel. As alterações são
          guardadas localmente neste navegador e refletem imediatamente nas páginas.
        </p>
      </div>

      <div className={cn('space-y-10', !hydrated && 'pointer-events-none opacity-70')}>
        {PAINEL_TEXT_SECTIONS.map((section) => {
          const entries = PAINEL_TEXT_ENTRIES.filter((e) => e.sectionId === section.id);
          if (entries.length === 0) return null;

          return (
            <section key={section.id} className="space-y-4">
              <h2 className="font-poppins text-xs font-bold uppercase tracking-[0.16em] text-sky-300/55">
                {section.label}
              </h2>

              <div className="space-y-4">
                {entries.map((entry) => {
                  const value = getText(entry.key);
                  const def = getPainelTextDefault(entry.key);
                  const overridden = isOverridden(entry.key);

                  return (
                    <div key={entry.key} className={cn(painelSurfaceClass, 'space-y-4 p-5 md:p-6')}>
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-poppins text-sm font-semibold text-white">
                          {entry.label}
                        </p>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className={cn(painelButtonOutlineClass, 'h-8 px-2.5 text-xs')}
                          onClick={() => resetText(entry.key)}
                          disabled={!overridden}
                          title="Repor o texto padrão"
                        >
                          <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                          Repor padrão
                        </Button>
                      </div>

                      <div className="grid gap-2">
                        <Label
                          htmlFor={`text-title-${entry.key}`}
                          className="font-inter text-xs font-medium text-blue-100/90"
                        >
                          Título
                        </Label>
                        <input
                          id={`text-title-${entry.key}`}
                          value={value.title}
                          onChange={(e) => setText(entry.key, { title: e.target.value })}
                          className={painelInputClass}
                          placeholder={def.title}
                        />
                      </div>

                      <div className="grid gap-2">
                        <Label
                          htmlFor={`text-desc-${entry.key}`}
                          className="font-inter text-xs font-medium text-blue-100/90"
                        >
                          Descrição
                        </Label>
                        <textarea
                          id={`text-desc-${entry.key}`}
                          value={value.description}
                          onChange={(e) => setText(entry.key, { description: e.target.value })}
                          className={cn(painelTextareaClass, 'min-h-[88px]')}
                          placeholder={def.description}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
